import type { ComponentType, HTMLAttributes } from "react";
import { useMemo } from "react";
import type { BoxHTMLProps, BoxOptions } from "reakit";
import { VisuallyHidden } from "reakit";
import { ArrowRight } from "@mos-cat/ds-icons";
import { createHook } from "reakit-system";
import type { DisplayProps } from "styled-system";
import type { SystemStyleObject } from "@styled-system/css";
import type { BoxProps } from "../Box/Box";
import { Box, useBox } from "../Box/Box";
import { focus } from "../utils/focusable";
import type { StylesOptions } from "../hooks/useStyles";
import type { supportedLanguages } from "./text";
import { createLanguageGetter } from "./text";

import { useRequiredPropWarning } from "../hooks/useRequiredPropWarning";
import pxToRem from "../utils/pxToRem";
import { WHCM, WHCMstyles } from "../constants";
import { getNumbers } from "./helpers";

type CustomLinkProps = HTMLAttributes<HTMLAnchorElement> & {
  href: string;
};

export type PaginationProps = {
  total: number;
  current: number;
  getHref: (page: number) => string;
  lang?: keyof typeof supportedLanguages;
  linkComponent?: ComponentType<CustomLinkProps>;
  linkProps?: HTMLAttributes<HTMLAnchorElement>;
} & BoxProps;

type CommonItemProps = {
  href: string;
  lang: keyof typeof supportedLanguages;
  linkProps?: HTMLAttributes<HTMLAnchorElement>;
  linkComponent?: ComponentType<CustomLinkProps>;
};

type PageItemProps = CommonItemProps & {
  children: number;
  isActive: boolean;
} & Required<DisplayProps>;

type ToStartItemProps = CommonItemProps;
type NextItemProps = CommonItemProps;

const itemStyles = {
  height: pxToRem(40),
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "pageBg",
  borderWidth: 1,
  borderColor: "pageBg",
  borderRadius: "paginationBorderRadius",
  color: "pageColor",
  px: "sp-4",
  py: "sp-8",
  fontSize: "paginationFontSize",
  lineHeight: "paginationLineHeight",

  "&:hover": {
    backgroundColor: "pageHoverBg",
    borderColor: "pageHoverBg",
  },
  "&:active": {
    backgroundColor: "pageActiveBg",
    borderColor: "pageActiveBg",
  },
  "&:focus": focus,

  "&[aria-current]": {
    backgroundColor: "pageCurrentBg",
    borderColor: "pageCurrentBg",

    "&:hover": {
      backgroundColor: "pageCurrentHoverBg",
      borderColor: "pageCurrentHoverBg",
    },
    "&:active": {
      backgroundColor: "pageCurrentActiveBg",
      borderColor: "pageCurrentActiveBg",
    },
  },

  [WHCM]: {
    color: "WindowText",

    "&[aria-current], &[aria-current]:hover, &[aria-current]:active": {
      ...WHCMstyles,
      backgroundColor: "WindowText",
      borderColor: "WindowText",
      color: "Window",
    },
  },
};

const useItem = createHook<
  BoxOptions & StylesOptions & Partial<Pick<PageItemProps, "isActive">>,
  BoxHTMLProps
>({
  name: "PaginationItem",
  keys: ["isActive"],
  compose: [useBox],
  useOptions(options) {
    return {
      ...options,
      styles: {
        ...itemStyles,
        ...options.styles,
      },
    };
  },
  useProps(options, props) {
    return {
      ...props,
      "aria-current": options.isActive ? "true" : undefined,
    };
  },
});

function PageItem({
  children,
  href,
  isActive,
  linkProps,
  linkComponent,
  display,
  lang,
}: PageItemProps) {
  const getText = useMemo(() => createLanguageGetter(lang), [lang]);
  const itemProps = useItem({
    isActive,
    styles: {
      display,
      width: pxToRem(40),
      mr: "sp-4",
    } as SystemStyleObject,
  });
  const Item = linkComponent ?? "a";
  return (
    <Box as="li" mt="sp-0">
      <Item href={href} {...itemProps} {...linkProps}>
        <VisuallyHidden>{getText("page")}</VisuallyHidden>
        {children}
      </Item>
    </Box>
  );
}

function ToStartItem({ href, linkProps, linkComponent, lang }: ToStartItemProps) {
  const getText = useMemo(() => createLanguageGetter(lang), [lang]);
  const itemProps = useItem(
    {
      styles: {
        minWidth: pxToRem(40),
        px: ["sp-4", "sp-12"],
        display: "flex",
        borderRadius: "paginationBorderRadius",
        backgroundColor: "white",
        mr: "sp-4",
      },
    },
    linkProps
  );
  const Item = linkComponent ?? "a";
  return (
    <>
      <Box as="li" mt="sp-0">
        <Item href={href} {...itemProps}>
          <Box as="span" display={["inline", "none"]}>
            <VisuallyHidden>{getText("toFirst")}</VisuallyHidden>
            <Box as="span" aria-hidden>
              1
            </Box>
          </Box>
          <Box as="span" display={["none", "inline"]}>
            {getText("toFirst")}
          </Box>
        </Item>
      </Box>
      <Box
        as="li"
        aria-hidden
        display={["flex", "none"]}
        tabIndex={-1}
        width={pxToRem(40)}
        alignItems="center"
        justifyContent="center"
        mr="sp-4"
        my="sp-0"
      >
        ...
      </Box>
    </>
  );
}

function NextItem({ href, linkProps, linkComponent, lang }: NextItemProps) {
  const getText = useMemo(() => createLanguageGetter(lang), [lang]);
  const Item = linkComponent ?? "a";
  const itemProps = useItem(
    {
      styles: {
        px: "sp-12",
        py: "sp-8",
        borderRadius: "paginationBorderRadius",
        display: "flex",
        minWidth: pxToRem(64),
      },
    },
    linkProps
  );

  return (
    <Box as="li" mt="sp-0" flexGrow={[1, 0]} display="flex" justifyContent="flex-end">
      <Item href={href} {...itemProps}>
        <VisuallyHidden>{getText("nextPage")}</VisuallyHidden>
        <Box as={ArrowRight} display={["block", "none"]} />

        <Box aria-hidden as="span" display={["none", "block"]}>
          {getText("next")}
        </Box>
      </Item>
    </Box>
  );
}

export function Pagination({
  total,
  current,
  getHref,
  linkProps,
  linkComponent,
  lang = "ru",
  ...other
}: PaginationProps) {
  const getText = useMemo(() => createLanguageGetter(lang), [lang]);
  useRequiredPropWarning(Boolean(total), "Pagination", "total");
  useRequiredPropWarning(Boolean(current) || current === 0, "Pagination", "current");
  useRequiredPropWarning(Boolean(getHref), "Pagination", "getHref");
  const showToStart = current >= 4 && total > 5;
  const showNext = current < total && total > 5;
  const pageNumbers = getNumbers(current, total);
  const pageNumbersElements = pageNumbers.map(({ value, display }) => (
    <PageItem
      display={display}
      key={value}
      isActive={value === current}
      linkProps={linkProps}
      href={getHref(value)}
      linkComponent={linkComponent}
      lang={lang}
    >
      {value}
    </PageItem>
  ));

  return (
    <Box
      as="nav"
      {...other}
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- нужна проверка на пустое значение
      aria-label={other["aria-label"] || getText("title")}
    >
      <Box
        as="ul"
        display="flex"
        alignItems="center"
        styles={{ listStyleType: "none" }}
        padding="sp-0"
        margin="sp-0"
      >
        {showToStart && (
          <ToStartItem
            linkComponent={linkComponent}
            linkProps={linkProps}
            href={getHref(1)}
            lang={lang}
          />
        )}
        {pageNumbersElements}
        {showNext && (
          <NextItem
            linkComponent={linkComponent}
            linkProps={linkProps}
            href={getHref(current + 1)}
            lang={lang}
          />
        )}
      </Box>
    </Box>
  );
}
